// `redirect` — out to another website and back (docs/plans/wizard-external-
// website-step.md §6). The server mints a single-use callback and builds the
// outside URL from the MODEL (never from the browser); this screen opens it
// and waits for the other site to post back to the callback.
//
// The window is opened on the user's click — blank first, then pointed at the
// URL once the server answered — so no popup blocker stops it. A POST is an
// auto-submitting form targeted at that window. While it waits the screen asks
// the server every few seconds (a database read, never a call to the other
// site), and at once when the callback page in the popup says it is done.

import { useCallback, useEffect, useRef, useState } from 'react'
import { ExternalLink, Loader2, RotateCcw } from 'lucide-react'
import { text } from '../model'
import { BAD, INFO, banner, button, muted } from '../styles'
import type { StepProps } from './common'

type State = 'idle' | 'opening' | 'waiting' | 'expired' | 'error'

const FAST_MS = 2000
const SLOW_MS = 5000
const SLOW_AFTER_MS = 120_000

export default function RedirectStep({ ctl, handheld }: StepProps) {
  const { step, scope, busy, run } = ctl
  const [state, setState] = useState<State>('idle')
  const [message, setMessage] = useState<string>('')
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null)
  const startedAt = useRef<number>(0)
  const alive = useRef(true)
  const winName = `twasta-redirect-${run?.run_id || 'run'}`

  const stop = () => {
    if (timer.current) clearTimeout(timer.current)
    timer.current = null
  }

  const poll = useCallback(async () => {
    stop()
    if (!alive.current) return
    let res: Awaited<ReturnType<typeof ctl.redirectPoll>>
    try {
      res = await ctl.redirectPoll()
    } catch (err) {
      if (typeof console !== 'undefined') console.warn('[wizard] redirect poll failed', err)
      res = 'waiting'
    }
    if (!alive.current) return
    if (res === 'done' || res === 'failed') { setState('idle'); return }
    if (res === 'expired') { setState('expired'); return }
    if (res === 'none') return
    const wait = Date.now() - startedAt.current > SLOW_AFTER_MS ? SLOW_MS : FAST_MS
    timer.current = setTimeout(() => { void poll() }, wait)
  }, [ctl])

  // A reload / a resumed run: the other site may already have answered.
  useEffect(() => {
    alive.current = true
    startedAt.current = Date.now()
    void (async () => {
      const res = await ctl.redirectPoll().catch(() => 'none' as const)
      if (!alive.current) return
      if (res === 'waiting') { setState('waiting'); timer.current = setTimeout(() => { void poll() }, FAST_MS) }
      else if (res === 'expired') setState('expired')
    })()
    const onMessage = (ev: MessageEvent) => {
      const d = ev.data as { type?: unknown } | null
      if (d && typeof d === 'object' && d.type === 'twasta-wizard-callback') void poll()
    }
    window.addEventListener('message', onMessage)
    return () => {
      alive.current = false
      stop()
      window.removeEventListener('message', onMessage)
    }
    // Re-armed per step entry only.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [step?.id])

  const open = async () => {
    // Opened synchronously on the click, so the browser lets it through.
    const win = window.open('about:blank', winName)
    setState('opening')
    setMessage('')
    const res = await ctl.redirectOpen()
    if (!res.ok || !res.url) {
      try { win?.close() } catch { /* already gone */ }
      setState('error')
      setMessage(res.error || 'The other site could not be opened.')
      return
    }
    if (res.method === 'POST') {
      const form = document.createElement('form')
      form.method = 'POST'
      form.action = res.url
      form.target = winName
      form.style.display = 'none'
      for (const [k, v] of Object.entries(res.form || {})) {
        const field = document.createElement('input')
        field.type = 'hidden'
        field.name = k
        field.value = v
        form.appendChild(field)
      }
      document.body.appendChild(form)
      form.submit()
      form.remove()
    } else if (win) {
      win.location.href = res.url
    } else {
      // The browser refused the window: go there in this tab is not an
      // option (the run would be left), so say so.
      setState('error')
      setMessage('Your browser blocked the window. Allow pop-ups for this site and try again.')
      return
    }
    startedAt.current = Date.now()
    setState('waiting')
    timer.current = setTimeout(() => { void poll() }, FAST_MS)
  }

  if (!step || !scope) return null
  const blk = step.redirect || {}
  const caption = blk.button ? text(blk.button, scope) : 'Open the other site'
  const working = !!busy || state === 'opening'

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
      {step.help && <div style={muted}>{text(step.help, scope)}</div>}
      {state === 'waiting' && (
        <div style={banner(INFO)}>
          <Loader2 size={16} className="spin" />
          <span>Waiting for the other site… finish there, and this screen continues by itself.</span>
        </div>
      )}
      {state === 'expired' && (
        <div style={banner(BAD)}>
          No reply came back in time. Open the site again to start over.
        </div>
      )}
      {state === 'error' && message && (
        <div style={banner(BAD)}>{message}</div>
      )}
      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
        <button type="button" style={button(handheld, state !== 'waiting', working)} disabled={working}
          onClick={() => void open()} data-tw-action="redirect-open">
          {working ? <Loader2 size={16} className="spin" /> : <ExternalLink size={16} />}
          {state === 'waiting' || state === 'expired' ? 'Open again' : caption}
        </button>
        {state === 'waiting' && (
          <button type="button" style={button(handheld)} disabled={working}
            onClick={() => void poll()} data-tw-action="redirect-check">
            <RotateCcw size={16} /> Check now
          </button>
        )}
      </div>
    </div>
  )
}
